import React, { Dispatch, useContext, useLayoutEffect, useState } from 'react';

import {
  paymentMethod,
  billingInfo,
  planDetails,
  invoiceHistory,
  notificationEmails,
  userDetails,
  redirectsWeb,
  redirectsMobile,
  leaderboardList,
  teammatesList,
  invitationsList,
  reportsList,
  dashboardChars,
  notifications,
  magicLink,
  upgradeCalculation,
  upgradeDetails,
  PaymentMethodInstance,
  BillingInfoInstance,
  PlanDetailsInstance,
  InvoiceHistoryInstance,
  NotificationEmailsInstance,
  UserDetailsInstance,
  RedirectsWebInstance,
  RedirectsMobileInstance,
  LeaderboardListInstance,
  TeammatesListInstance,
  InvitationsListInstance,
  ReportsListInstance,
  DashboardChartsInstance,
  NotificationsInstance,
  MagicLinkInstance,
  UpgradeCalculationInstance,
  UpgradeDetailsInstance,
} from 'models';
import keys, { KeysInstance } from 'models/keys/keys';
import { UserBillingType, UserRole } from 'globals/types';
import defaultLocales from 'globals/locales/default';
import {
  delSessionVerified,
  getSessionVerified,
  setSessionVerified,
} from 'globals/utils';

type AppContextInterface = {
  // Stores
  dashboardChars: DashboardChartsInstance;
  reportsList: ReportsListInstance;
  invitationsList: InvitationsListInstance;
  teammatesList: TeammatesListInstance;
  leaderboardList: LeaderboardListInstance;
  keys: KeysInstance;
  redirectsMobile: RedirectsMobileInstance;
  redirectsWeb: RedirectsWebInstance;
  notificationEmails: NotificationEmailsInstance;
  userDetails: UserDetailsInstance;
  paymentMethod: PaymentMethodInstance;
  billingInfo: BillingInfoInstance;
  planDetails: PlanDetailsInstance;
  invoiceHistory: InvoiceHistoryInstance;
  notifications: NotificationsInstance;
  magicLink: MagicLinkInstance;
  upgradeCalculation: UpgradeCalculationInstance;
  upgradeDetails: UpgradeDetailsInstance;

  // Flags
  isStaging?: boolean;
  isVerified?: boolean;
  setIsVerified: Dispatch<boolean>;
  isBlocked?: boolean;
  setIsBlocked: Dispatch<boolean>;
  isUpgraded?: boolean;
  setIsUpgraded: Dispatch<boolean>;
  isSubscribed?: boolean;
  setIsSubscribed: Dispatch<boolean>;
  isTrial?: boolean;
  setIsTrial: Dispatch<boolean>;
  isPaymentMethodIssue?: boolean;
  setIsPaymentMethodIssue: Dispatch<boolean>;

  // Types
  userRole?: UserRole;
  setUserRole: Dispatch<UserRole>;
  userBillingType?: UserBillingType;
  setUserBillingType: Dispatch<UserBillingType>;
};

export const AppContext = React.createContext<AppContextInterface>({
  // Stores
  dashboardChars,
  reportsList,
  invitationsList,
  teammatesList,
  leaderboardList,
  keys,
  redirectsMobile,
  redirectsWeb,
  notificationEmails,
  userDetails,
  paymentMethod,
  billingInfo,
  planDetails,
  invoiceHistory,
  notifications,
  magicLink,
  upgradeCalculation,
  upgradeDetails,

  // Flags
  isStaging: undefined,
  isVerified: undefined,
  setIsVerified: () => undefined,
  isBlocked: undefined,
  setIsBlocked: () => undefined,
  isUpgraded: undefined,
  setIsUpgraded: () => undefined,
  isTrial: undefined,
  setIsTrial: () => undefined,
  isSubscribed: undefined,
  setIsSubscribed: () => undefined,
  isPaymentMethodIssue: undefined,
  setIsPaymentMethodIssue: () => undefined,

  // Types
  userRole: undefined,
  setUserRole: () => undefined,
  userBillingType: undefined,
  setUserBillingType: () => undefined,
});

const AppContextProvider: React.FC = ({ children }) => {
  const wasVerifiedBefore = getSessionVerified();
  const [isVerified, setIsVerified] = useState<boolean>(wasVerifiedBefore);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isUpgraded, setIsUpgraded] = useState<boolean>(false);
  const [isTrial, setIsTrial] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isPaymentMethodIssue, setIsPaymentMethodIssue] = useState<boolean>(
    false
  );
  const [userRole, setUserRole] = useState<UserRole>(UserRole.ADMIN);
  const [userBillingType, setUserBillingType] = useState<UserBillingType>(
    UserBillingType.STANDARD
  );

  useLayoutEffect(() => {
    if (isVerified) {
      setSessionVerified();
    } else {
      delSessionVerified();
    }
  }, [isVerified]);

  const values = {
    // Stores
    dashboardChars,
    reportsList,
    invitationsList,
    teammatesList,
    leaderboardList,
    keys,
    redirectsMobile,
    redirectsWeb,
    notificationEmails,
    userDetails,
    paymentMethod,
    billingInfo,
    planDetails,
    invoiceHistory,
    notifications,
    magicLink,
    upgradeCalculation,
    upgradeDetails,

    // Flags
    isStaging: true,
    isVerified,
    setIsVerified,
    isBlocked,
    setIsBlocked,
    isUpgraded,
    setIsUpgraded,
    isTrial,
    setIsTrial,
    isSubscribed,
    setIsSubscribed,
    isPaymentMethodIssue,
    setIsPaymentMethodIssue,

    // Types
    userRole,
    setUserRole,
    userBillingType,
    setUserBillingType,
  };

  return <AppContext.Provider value={values}>{children}</AppContext.Provider>;
};

export const useAppContext = (): AppContextInterface => {
  const appContext = useContext(AppContext);

  if (appContext === null) {
    throw new Error(defaultLocales.appContextNotificationError);
  }

  return {
    ...appContext,
  };
};

export default AppContextProvider;
